import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Linking, Alert, Platform} from 'react-native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Image,
  ScrollView,
  Dimensions
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/FontAwesome';
import io from 'socket.io-client';
import {runAssistantWorkflow} from '../helpers/chatBotFunctions';
import axios from 'axios';
import { fontFamily } from '../helpers/fontFamily';
import useWidth from './scanToCartScanning/useWidth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const ChatIconSocket = () => {
  const insets = useSafeAreaInsets();
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [userInfo, setUserInfo] = useState('');
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState('');
  const [isAiChat, setIsAiChat] = useState(true);
  const [placeHolderText, setPlaceHolderText] = useState('Type a message...');
  const [loading, setLoading] = useState(false);
  const [nextButtonShow, setNextButtonShow] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isEmailStored, setIsEmailStored] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [storedThreadid, setStoredThreadid] = useState('');
  const [inputHeight, setInputHeight] = useState(21);
  const [isSupportContacted, setIsSupportContacted] = useState(false);
  const [isChatSentToSlack, setIsChatSentToSlack] = useState(false);
  const userInfoRef = useRef('');
  const userNameRef = useRef('');

  const socketRef = useRef(null);
  const scrollViewRef = useRef(null);
  const chatInputRef = useRef(null);
  const slideAnim = useRef(new Animated.Value(0)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  const [showButtons, setShowButtons] = useState(false);
  const flatListRef = useRef(null);
  const [selectedChatMode, setSelectedChatMode] = useState('bot');
  const [scrollIndicator, setScrollIndicator] = useState(new Animated.Value(0));
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(false);
  const [scrollIndicatorHeight, setScrollIndicatorHeight] = useState(0);
  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  const screenWidthHook = useWidth();
  const NAVBAR_CONTENT_HEIGHT = 56;
  const NAVBAR_BOTTOM_OFFSET = screenWidthHook >= 1024 ? 6 : 18;
  const chatIconButtonSize = screenWidthHook >= 1024 ? 40 : 34;
  const navBarBottom = Math.max(insets.top, 0) + NAVBAR_CONTENT_HEIGHT;
  const chatIconTop = Math.max(
    navBarBottom - chatIconButtonSize - NAVBAR_BOTTOM_OFFSET,
    insets.top
  );
  const webSafeTop = `calc(env(safe-area-inset-top, 0px) + ${NAVBAR_CONTENT_HEIGHT}px - ${
    chatIconButtonSize + NAVBAR_BOTTOM_OFFSET
  }px)`;
  const chatButtonPosition =
    Platform.OS === 'web'
      ? [
          screenWidthHook >= 1024
            ? styles.buttonContainerWebLarge
            : styles.buttonContainerWebSmall,
          {
            top: webSafeTop,
            right: 20
          }
        ]
      : [
          styles.buttonContainerMobile,
          {
            top: chatIconTop,
            right: 20
          }
        ];

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const info = await AsyncStorage.getItem('userInfo');
        const storedEmail = await AsyncStorage.getItem('storedEmail');
        const threadId = await AsyncStorage.getItem('threadId');

        if (storedEmail) {
          setIsEmailStored(true);
          setUserEmail(storedEmail);
        }

        if (info) {
          const parsedInfo = JSON.parse(info);
          setUserInfo(parsedInfo.current_tenant);
          setUserName(parsedInfo.username);
          setUserId(parsedInfo.id);
        }

        if (threadId) {
          setStoredThreadid(threadId);
        } else {
          setStoredThreadid('');
        }
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }
    };
    fetchUserDetails();
  }, [isChatVisible, userInfo, userName]);

  // Separate useEffect to fetch chat data after user state is updated
  useEffect(() => {
    if (userId && userName && userInfo) {
      // Fetch all messages for the current user
      socketRef.current.emit('getData', {
        tenantName: userInfo,
        userName: userName
      });

      // Listen for incoming chat data
      socketRef.current.on('chatData', messagesData => {
        if (Array.isArray(messagesData)) {
          const formattedMessages = messagesData.map(msg => ({
            id: msg._id,
            name: msg.user,
            messageContent: msg.message,
            type: msg.from === 'app' ? 'user' : 'employee' // Distinguish chatbot and Slack messages
          }));
          setMessages(formattedMessages);
        } else if (messagesData.error) {
          console.error('Error fetching chat data:', messagesData.error);
        }
      });

       // Function to check for midnight and reset chat mode
       const checkMidnightReset = () => {
        const now = new Date();
        if (now.getHours() === 0 && now.getMinutes() === 0) {
          setIsAiChat(true); // Switch back to AI mode
          setSelectedChatMode('bot');
          setPlaceHolderText('Type a message...');
  
          // Notify user about the reset
          setMessages(prevMessages => [
            ...prevMessages,
            {
              name: 'GPBOT',
              messageContent: 'Your chat has been reset to AI mode at midnight. You can start a new conversation.',
              type: 'bot'
            }
          ]);
  
          console.log("Chat mode reset to AI at midnight.");
        }
      };
  
      // Check every minute to trigger reset if it's midnight
      const midnightInterval = setInterval(checkMidnightReset, 60000); // Runs every 60 seconds
  
      return () => {
        socketRef.current.off('chatData'); // Clean up listener
        clearInterval(midnightInterval); // Clear the midnight check interval
      };
    }
  }, [userId, userName, userInfo]); // Ensure it runs when user state changes

  const capitalizeName = name => {
    if (!name) return '';
    return name
      .split('.')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('.');
  };

  // Initialize socket connection once
  useEffect(() => {
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL ||
      'https://gpxbackend-c9a99685c215.herokuapp.com';

    socketRef.current = io(socketUrl);

    const socket = socketRef.current;
    socket.on('connect', () => {});
    socket.on('disconnect', () => {});

    socket.on('slackMessage', message => {
      handleSlackMessage(message);
    });

    socket.on('userJoined', data => {
      handleUserJoined(data);
    });
    socket.on('chatData', messagesData => {
      if (Array.isArray(messagesData)) {
        const newMessagesArray = messagesData?.map(obj => {
          return {
            id: obj._id,
            name: obj.user,
            messageContent: obj.message,
            type: obj.from === 'app' ? 'user' : 'employee'
          };
        });
        if (newMessagesArray.length > 0) {
          const lastMessage = messages.filter(
            message =>
              message.id == newMessagesArray[newMessagesArray.length - 1].id
          );
          if (lastMessage.length === 0) {
            setMessages(prevMessages => [...prevMessages, ...newMessagesArray]);
          }
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    userInfoRef.current = userInfo;
    userNameRef.current = userName;
  }, [userInfo, userName]);

  const handleSlackMessage = data => {
    const currentUserInfo = userInfoRef.current;
    const currentUserName = userNameRef.current;
    const channeName = `gp-${currentUserInfo}-${currentUserName}`;
    if (channeName === data.channelName) {
      setMessages(prevMessages => [...prevMessages, data]);
    }
  };

  const renderMessageContent = messageContent => {
    const urlRegex = /<?(https?:\/\/[^\s<>]+)>?/g;

    return messageContent.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        const sanitizedURL = decodeURIComponent(
          part.trim().replace(/^<|>$/g, '')
        );
        console.log('Sanitized URL:', sanitizedURL);

        return (
          <Text
            key={index}
            style={{color: 'blue', textDecorationLine: 'underline'}}
            onPress={async () => {
              try {
                const supported = await Linking.canOpenURL(sanitizedURL);

                if (supported) {
                  await Linking.openURL(sanitizedURL);
                } else {
                  Alert.alert('Error', 'This URL cannot be opened.');
                }
              } catch (error) {
                Alert.alert(
                  'Error',
                  'An unexpected error occurred while opening the URL.'
                );
                console.error('Error opening URL:', error);
              }
            }}
          >
            {sanitizedURL}
          </Text>
        );
      }
      return part;
    });
  };

  const handleUserJoined = useCallback(
    data => {
      const currentUserInfo = userInfoRef.current;
      const currentUserName = userNameRef.current;
      const channeName = `gp-${currentUserInfo}-${currentUserName}`;
      if (channeName === data.channelName) {
        if (data.user !== 'demo_app') {
          const newMessage = {
            name: data?.user?.charAt(0).toUpperCase() + data?.user?.slice(1),
            messageContent: data.message,
            type: 'employee'
          };
          setMessages(prev => [...prev, newMessage]);
          setIsAiChat(false);
        }
      }
    },
    [userInfo, userName]
  );

  const toggleChatVisibility = useCallback(() => {
    if (!isChatVisible) {
      global.chatActive = true;  // Chat just opened
      setPlaceHolderText('Type a message...');
    } else {
      global.chatActive = false; // Chat just closed
    }
    setIsChatVisible(!isChatVisible);

    if (isChatVisible) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true
        }),
        Animated.timing(opacityAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true
        })
      ]).start(() => {
        setIsChatVisible(false);
      });
    } else {
      setIsChatVisible(true);
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true
        })
      ]).start(() => setIsChatVisible(true));
    }
  }, [isChatVisible]);

  useEffect(() => {
    if (userInfo && userName) {
      getDataHandler();
    }
  }, [userInfo, userName, getDataHandler]);

  const getDataHandler = () => {
    try {
      socketRef.current.emit('getData', {
        tenantName: userInfo,
        userName: userName
      });
    } catch (error) {}
  };

  const sendMessage = useCallback(() => {
    if (!newMessage.trim()) return;

    const newMsg = {
      id: (messages.length + 1).toString(),
      name: 'User',
      messageContent: newMessage.trim(),
      type: 'user'
    };
    setMessages(prev => {
      if (!prev.find(msg => msg.id === newMsg.id)) {
        return [...prev, newMsg];
      }
      return prev;
    });

    // Only thank you message and history sent to Slack after email is entered
    if (isSupportContacted && !sessionStorage.getItem('thankYouSent')) {
      setMessages(prevMessages => [
        ...prevMessages,
        {
          name: 'GPBOT',
          messageContent:
            'Thank you! Your chat has been shared with support. Our support team will get back to you shortly.',
          type: 'bot'
        }
      ]);

      sessionStorage.setItem('thankYouSent', 'true');

      // Send chat history to Slack
      sendChatToSupport(userEmail); // This should be triggered only once, when the email is entered
    }

    try {
      socketRef.current.emit('chatMessageTrigger', {
        userid: userId,
        tenantName: userInfo,
        userName: userName,
        message: newMessage,
        email: userEmail,
        botMessage: false,
        emailSubmitted: isSupportContacted
      });

      setNewMessage('');
      setInputHeight(21);
      setPlaceHolderText('Type a message...');
    } catch (error) {
      const replyMsg = {
        messageContent: 'Sorry some error occurred',
        type: 'bot',
        name: 'GPBOT',
        id: (messages.length + 2).toString()
      };
      setMessages(prevMessages => [...prevMessages, replyMsg]);
    }
  }, [
    newMessage,
    messages,
    userId,
    userInfo,
    userName,
    userEmail,
    isSupportContacted
  ]);

  const sendAiMessage = useCallback(async () => {
    if (!newMessage.trim()) return; // Ignore empty messages

    // Add user message to chat UI
    const messageData = {
      id: (messages.length + 1).toString(),
      name: 'User',
      messageContent: newMessage.trim(),
      type: 'user'
    };
    setMessages(prev => [...prev, messageData]);

    // Send user message to Slack
    socketRef.current.emit('chatMessageTrigger', {
      userid: userId,
      tenantName: userInfo,
      userName: userName,
      message: newMessage,
      email: userEmail,
      botMessage: false // Marking as user message
    });

    // Reset input
    setNewMessage('');
    setInputHeight(21);

    // AI chat response processing
    if (isAiChat) {
      try {
        setLoading(true);
        const data = await runAssistantWorkflow(newMessage, storedThreadid);

        // Update thread ID if received
        if (data?.idThread) {
          setStoredThreadid(data.idThread);
        }

        if (data?.aiResponse) {
          const botReplyMessage =
            data.aiResponse.data[0]?.content[0]?.text?.value ||
            'I didn’t understand that. Could you please rephrase?';

          const botReply = {
            id: (messages.length + 2).toString(),
            name: 'GPBOT',
            messageContent: botReplyMessage,
            type: 'bot'
          };

          setMessages(prev => [...prev, botReply]);

          // Send AI response to Slack
          socketRef.current.emit('chatMessageTrigger', {
            userid: userId,
            tenantName: userInfo,
            userName: userName,
            message: botReplyMessage,
            email: userEmail,
            botMessage: true // Marking as bot message
          });

          // Step 3: Restore "Contact Support" and "Question Answered" buttons
          if (!botReplyMessage.includes('?')) {
            setTimeout(() => {
              setPlaceHolderText('Continue typing to ask follow-up questions.');
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  id: (prevMessages.length + 1).toString(),
                  name: 'user',
                  messageContent: 'Question Answered! Ask something else.',
                  type: 'newAiMessage'
                },
                {
                  id: (prevMessages.length + 2).toString(),
                  name: 'user',
                  messageContent: 'Contact Support about this issue.',
                  type: 'contactSupport'
                }
              ]);
              setShowButtons(true);
            }, 3000);
          }
        } else {
          throw new Error('No AI response received.');
        }
      } catch (error) {
        console.error('Error in AI response:', error);

        setMessages(prev => [
          ...prev,
          {
            id: (messages.length + 2).toString(),
            name: 'GPBOT',
            messageContent:
              'Sorry, some error occurred while processing your request.',
            type: 'bot'
          }
        ]);
      } finally {
        setLoading(false);
      }
    }
  }, [
    newMessage,
    messages,
    isAiChat,
    storedThreadid,
    userId,
    userInfo,
    userName,
    userEmail
  ]);

  const handleScroll = event => {
    const scrollPosition = event.nativeEvent.contentOffset.y;
    const contentHeight = event.nativeEvent.contentSize.height;
    const scrollViewHeight = event.nativeEvent.layoutMeasurement.height;

    const visibleRatio = scrollViewHeight / contentHeight;
    const newScrollIndicatorHeight = Math.max(
      visibleRatio * scrollViewHeight,
      20
    );
    const scrollIndicatorPosition =
      (scrollPosition / (contentHeight - scrollViewHeight)) *
      (scrollViewHeight - newScrollIndicatorHeight);

    Animated.timing(scrollIndicator, {
      toValue: scrollIndicatorPosition,
      duration: 0,
      useNativeDriver: false
    }).start();

    setScrollIndicatorHeight(newScrollIndicatorHeight);
    setScrollIndicatorVisible(contentHeight > scrollViewHeight);
  };

  const handleTextChange = useCallback(
    text => {
      setNewMessage(text);

      const charWidth = 7;
      const inputWidth = 292;
      const lineBreaks = text.split('\n').length;
      const charsPerLine = Math.floor(inputWidth / charWidth);
      const wrappedLines = Math.ceil(text.length / charsPerLine);
      const totalLines = Math.max(lineBreaks, wrappedLines);
      const calculatedHeight = Math.min(100, 21 * totalLines);
      setInputHeight(calculatedHeight);

      if (text === '') {
        setInputHeight(21);
      }
    },
    [newMessage]
  );

  // const chatTranslateY = slideAnim.interpolate({
  //   inputRange: [0, 1],
  //   outputRange: [40, 0] // Change 500 to the height you want the chat to slide from
  // });

  // Auto-focus the TextInput when the chat becomes visible
  useEffect(() => {
    if (isChatVisible && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isChatVisible]);

  const handleChatFocus = () => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  };

  const handleContentSizeChange = event => {
    const {height, width} = event.nativeEvent.contentSize;
    // if (width) {
    //   console.log('------------new----width:::', width, height);

    //   setInputHeight(height + 21);
    // }

    // const numberOfLines = Math.ceil(width / inputWidth); // Calculate number of wrapped lines
    // const calculatedHeight = Math.min(100, height + 21 * (numberOfLines - 1));

    // setInputHeight(calculatedHeight);

    // setInputHeight(Math.min(100, height)); // Set a max height if needed

    // setInputHeight(Math.min(height, 100));
    // if (height > 21 && height <= 100) {
    //   setInputHeight(height);
    // } else if (height > 100) {
    //   setInputHeight(100);
    // } else {
    //   setInputHeight(21);
    // }
  };

  const agentChatHandler = useCallback(() => {
    setIsAiChat(false); // Switch to agent mode
    setSelectedChatMode('agent');
  
    // Remove previous "Contact Support" buttons if present
    const updatedMessages = messages.filter(
      msg => msg.type !== 'contactSupport'
    );
    setMessages(updatedMessages);
  
    // **Emit Contact Support Event Immediately**
    socketRef.current.emit('contactSupport', {
      tenantName: userInfo,
      userName: userName,
      emailSubmitted: false, // Ensures first message is sent
    });
  
    // Prompt user to enter email
    setMessages(prevMessages => [
      ...prevMessages,
      {
        name: 'GPBOT',
        messageContent:
          'Please enter your email address so we can assist you further.',
        type: 'bot'
      }
    ]);
  
    setPlaceHolderText('Enter your email here');
    setNextButtonShow(true); // Show the email input field
  }, [messages]);  

  const backEndURL =
    process.env.REACT_APP_API_URL || 'https://gpxbackend-c9a99685c215.herokuapp.com';
  const newAiChatHandler = useCallback(() => {
    AsyncStorage.removeItem('threadId');
    setStoredThreadid('');
    setIsAiChat(true);
    setSelectedChatMode('bot');
    setPlaceHolderText('Type a message...');
    setMessages([]);

    const channelName = `GP-${userInfo}-${userName}`;
    axios({
      method: 'delete',
      url: `${backEndURL}/api/clearChat/${channelName}`
    })
      .then(res => {
        if (res.status === 200) {
        }
      })
      .catch(error => {});
  }, []);

  const validateEmail = useCallback(() => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setIsEmailValid(emailRegex.test(newMessage));
    return emailRegex.test(newMessage);
  }, [newMessage]);

  const nextButtonHandler = useCallback(async () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newMessage)) {
      setIsEmailValid(false); // Indicate invalid email
      return;
    }

    setIsEmailValid(true); // Valid email
    setUserEmail(newMessage); // Save the entered email temporarily
    setIsSupportContacted(true); // Mark support as contacted after email is entered
    setNewMessage('');

    // Remove the "Please enter your email..." message
    setMessages(prevMessages =>
      prevMessages.filter(
        msg =>
          msg.messageContent !==
          'Please enter your email address so we can assist you further.'
      )
    );

    // Send chat history only if it hasn't been sent before
    if (!isChatSentToSlack) {
      sendChatToSupport(newMessage); // Send chat to Slack right away
      setIsChatSentToSlack(true); // Ensure chat is only sent once
    }

    // Step 1: Ask for follow-up question (this will NOT trigger sending to Slack)
    setMessages(prevMessages => [
      ...prevMessages,
      {
        name: 'GPBOT',
        messageContent: 'Enter any follow-up questions you have.',
        type: 'bot'
      }
    ]);

    setPlaceHolderText('Type your follow-up question here...');
    setNextButtonShow(false); // Hide the email input field
  }, [newMessage, messages, isChatSentToSlack]);

  const sendChatToSupport = email => {
    if (isChatSentToSlack) {
      console.warn(
        'Chat history has already been sent. Skipping duplicate send.'
      );
      return;
    }

    const chatHistory = messages.map(msg => ({
      name: msg.name,
      content: msg.messageContent,
      type: msg.type
    }));

    socketRef.current.emit('contactSupport', {
      email,
      tenantName: userInfo,
      userName,
      chatHistory,
      emailSubmitted: true // Mark email as submitted
    });

    console.log('Chat history sent to backend:', chatHistory);
    setIsChatSentToSlack(true); // Ensure this is set once
  };

  return (
    <View style={styles.rootContainer}>
      <TouchableOpacity
        style={[styles.buttonContainer, chatButtonPosition]}
        onPress={toggleChatVisibility}
      >
        {isChatVisible ? (
          <Icon name="times-circle" size={24} color="#fff" />
        ) : (
          <Icon name="comments" size={20} color="white" />
        )}
      </TouchableOpacity>

      {/* Chat Container */}
      {isChatVisible && (
        <LinearGradient
          colors={['#304454', '#142130']}
          style={[
            styles.borderChatContainer,
            {
              right: screenWidth > 460 ? 50 : 10,
              width: screenWidth > 460 ? 390 : 340,
              height: screenHeight > 690 ? 585 : 570
            }
          ]}
        >
          <View
            style={[
              styles.chatContainer,
              {
                height: screenHeight > 690 ? 585 : 570
              }
            ]}
          >
            {/* buttons */}
            {/* <View style={styles.toggleContainer}>
              
              <TouchableOpacity
                style={[
                  styles.toggleButton,
                  selectedChatMode === 'bot'
                    ? styles.selectedButton
                    : styles.unselectedButton
                ]}
                onPress={() => {
                  setSelectedChatMode('bot');
                  setNextButtonShow(false);
                  setIsAiChat(true);
                  setPlaceHolderText('Type a message...');
                  const updatedMessage = messages.filter(message => {
                    return !['bot', 'newAiMessage', 'contactSupport'].includes(
                      message.type
                    );
                  });
                  console.log('______updatedMessage::::', updatedMessage);
                  console.log('______updatedMessage::::', messages);
                  setMessages(updatedMessage);
                  setMessages(prevMessages => [
                    ...prevMessages,
                    {
                      name: 'GPBOT',
                      messageContent: 'Ask you queries from AI bot...',
                      type: 'bot'
                    }
                  ]);
                }}>
                <IconNew
                  name="robot"
                  size={12}
                  color={selectedChatMode === 'bot' ? '#fff' : '#0586CE'}
                  style={styles.icon}
                />
                <Text
                  style={[
                    styles.toggleText,
                    selectedChatMode === 'bot'
                      ? styles.selectedText
                      : styles.unselectedText
                  ]}>
                  Bot
                </Text>
              </TouchableOpacity>

              
              <TouchableOpacity
                style={[
                  styles.toggleButton,
                  selectedChatMode === 'agent'
                    ? styles.selectedButton
                    : styles.unselectedButton
                ]}
                onPress={() => {
                  setSelectedChatMode('agent');
                  setIsAiChat(false);
                  setPlaceHolderText('Type a message...');
                  const updatedMessage = messages.filter(message => {
                    return !['bot', 'newAiMessage', 'contactSupport'].includes(
                      message.type
                    );
                  });
                  setMessages(updatedMessage);

                  if (!isEmailStored) {
                    setNextButtonShow(true);
                    setMessages(prevMessages => [
                      ...prevMessages,
                      {
                        name: 'GPBOT',
                        messageContent:
                          'Please enter an email address that support can use to reach you.',
                        type: 'bot'
                      }
                    ]);
                    setPlaceHolderText('Enter you email here');
                  }
                }}>
                <Icon
                  name="user"
                  size={12}
                  color={selectedChatMode === 'agent' ? '#fff' : '#0586CE'}
                  style={styles.icon}
                />
                <Text
                  style={[
                    styles.toggleText,
                    selectedChatMode === 'agent'
                      ? styles.selectedText
                      : styles.unselectedText
                  ]}>
                  Agent
                </Text>
              </TouchableOpacity>
            </View> */}

            {/* If messages length is 0 */}
            {messages.length === 0 && (
              <View
                style={{
                  alignItems: 'center',
                  marginTop: 240
                }}
              >
                <Text style={{color: '#aaa', fontSize: 20}}>
                  Hello, how can I help you...
                </Text>
              </View>
            )}

            <View style={[styles.scrollViewContainer]}>
              <ScrollView
                ref={scrollViewRef}
                onContentSizeChange={() =>
                  scrollViewRef.current?.scrollToEnd({animated: false})
                }
                onScroll={handleScroll}
                scrollEventThrottle={16}
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
              >
                {messages?.map(item => {
                  const isUser =
                    item.type === 'user' ||
                    item.type === 'newAiMessage' ||
                    item.type === 'contactSupport';
                  const isBot = item.type === 'bot';
                  const borderColor = isUser
                    ? ['#0586CE', '#056EAA', '#045586']
                    : isBot
                    ? ['#8E53EE', '#BC5AF7', '#D35EFB']
                    : ['#7BFAC3', '#1CBB77'];

                  return (
                    <View
                      style={[
                        isUser
                          ? {alignItems: 'flex-start'}
                          : {alignItems: 'flex-end', marginRight: 18}
                      ]}
                      key={item.id}
                    >
                      {item.type === 'newAiMessage' ? (
                        // ------------------ button for new AI chat
                        <TouchableOpacity
                          style={{maxWidth: '86%'}}
                          onPress={newAiChatHandler}
                        >
                          <LinearGradient
                            colors={['#0586CE', '#056EAA', '#045586']}
                            style={[
                              {
                                marginBottom: 10,
                                width: '100%',
                                borderRadius: 10
                              }
                            ]}
                            key={item.id}
                          >
                            <View
                              style={[
                                {
                                  backgroundColor: 'black',
                                  margin: 1,
                                  borderRadius: 10,
                                  position: 'relative'
                                },
                                isUser ? '' : {minWidth: 100}
                              ]}
                            >
                              <View style={[styles.messageContainer]}>
                                <Text
                                  style={[
                                    styles.messageText,
                                    isUser ? '' : styles.companyTextWrapper
                                  ]}
                                >
                                  {item.messageContent}
                                </Text>
                              </View>
                            </View>
                          </LinearGradient>
                        </TouchableOpacity>
                      ) : item.type === 'contactSupport' ? (
                        // ------------------ button for agent chat
                        <TouchableOpacity
                          onPress={agentChatHandler}
                          style={{maxWidth: '86%'}}
                        >
                          <LinearGradient
                            colors={['#7BFAC3', '#1CBB77']}
                            style={[
                              {
                                marginBottom: 10,
                                width: '100%',
                                borderRadius: 10
                              }
                            ]}
                            key={item.id}
                          >
                            <View
                              style={[
                                {
                                  backgroundColor: 'black',
                                  margin: 1,
                                  borderRadius: 10,
                                  position: 'relative'
                                },
                                isUser ? '' : {minWidth: 100}
                              ]}
                            >
                              <View style={[styles.messageContainer]}>
                                <Text
                                  style={[
                                    styles.messageText,
                                    isUser ? '' : styles.companyTextWrapper
                                  ]}
                                >
                                  {item.messageContent}
                                </Text>
                              </View>
                            </View>
                          </LinearGradient>
                        </TouchableOpacity>
                      ) : (
                        <LinearGradient
                          colors={borderColor}
                          style={[
                            {
                              marginBottom: 10,
                              maxWidth: '86%',
                              borderRadius: 10
                            }
                          ]}
                          key={item.id}
                        >
                          <View
                            style={[
                              {
                                backgroundColor: 'black',
                                margin: 1,
                                borderRadius: 10,
                                position: 'relative'
                              },
                              isUser ? '' : {minWidth: 100}
                            ]}
                            key={item.id}
                          >
                            <View style={[styles.messageContainer]}>
                              {!isUser && (
                                <LinearGradient
                                  colors={borderColor}
                                  style={{
                                    borderRadius: 10,
                                    position: 'absolute',
                                    top: -1,
                                    left: -1
                                  }}
                                >
                                  <View
                                    style={{
                                      margin: 1,
                                      backgroundColor: 'black',
                                      borderRadius: 10,
                                      paddingHorizontal: 4,
                                      paddingVertical: 2
                                    }}
                                  >
                                    <Text style={styles.nameText}>
                                      {capitalizeName(item.name)}{' '}
                                      {/* Ensure the name is capitalized */}
                                    </Text>
                                  </View>
                                </LinearGradient>
                              )}
                              <View>
                                {/* Updated to make URLs clickable */}
                                <Text
                                  style={[
                                    styles.messageText,
                                    isUser ? '' : styles.companyTextWrapper
                                  ]}
                                >
                                  {renderMessageContent(item.messageContent)}
                                </Text>
                              </View>
                            </View>
                          </View>
                        </LinearGradient>
                      )}
                    </View>
                  );
                })}
              </ScrollView>

              {scrollIndicatorVisible && (
                <Animated.View
                  style={[
                    styles.scrollIndicator,
                    {top: scrollIndicator, height: scrollIndicatorHeight}
                  ]}
                />
              )}
            </View>

            {loading && (
              <View>
                <Text style={{color: 'white'}}>Thinking...</Text>
              </View>
            )}

            {nextButtonShow && (
              <View style={{flexDirection: 'row'}}>
                <TouchableOpacity onPress={nextButtonHandler}>
                  <LinearGradient
                    colors={['#7BFAC3', '#1CBB77']}
                    style={[
                      {
                        borderRadius: 10,
                        width: 70
                      }
                    ]}
                  >
                    <View
                      style={[
                        {
                          backgroundColor: 'black',
                          margin: 1,
                          borderRadius: 10,
                          position: 'relative'
                        }
                      ]}
                    >
                      <View
                        style={[
                          styles.messageContainer,
                          {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center'
                          }
                        ]}
                      >
                        <Text style={[styles.messageText, {marginRight: 8}]}>
                          Next
                        </Text>
                        <Icon name="chevron-right" size={12} color="#fff" />
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {!isEmailValid && (
                  <LinearGradient
                    colors={['#8E53EE', '#BC5AF7', '#D35EFB']}
                    style={[
                      {
                        borderRadius: 10,
                        marginLeft: 10,
                        width: 210
                      }
                    ]}
                  >
                    <View
                      style={[
                        {
                          backgroundColor: 'black',
                          margin: 1,
                          borderRadius: 10,
                          position: 'relative'
                        }
                      ]}
                    >
                      <View style={styles.messageContainer}>
                        <Text style={styles.messageText}>
                          Please enter a valid email
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                )}
              </View>
            )}

            <LinearGradient
              colors={['#0586CE', '#056EAA', '#045586']}
              style={styles.inputGradientBorder}
            >
              <View style={styles.inputContainer}>
                <TextInput
                  ref={chatInputRef}
                  style={[styles.input, {height: inputHeight}]}
                  value={newMessage}
                  onChangeText={handleTextChange}
                  placeholder={placeHolderText}
                  placeholderTextColor={
                    placeHolderText === 'Type a message...' ? '#fff' : '#373737'
                  }
                  multiline={true}
                  // onContentSizeChange={handleContentSizeChange}
                  numberOfLines={1}
                  onFocus={handleChatFocus}
                  autoFocus={true}
                  scrollEnabled={false}
                  textAlignVertical="top"
                  onKeyPress={({nativeEvent}) => {
                    if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
                      // if (disableSubmitButton) return;
                      nativeEvent.preventDefault();

                      if (nextButtonShow) {
                        nextButtonHandler();
                        return;
                      }
                      if (!loading) {
                        if (isAiChat && !nextButtonShow) {
                          sendAiMessage();
                        } else {
                          sendMessage();
                        }
                      }
                    }
                  }}
                />
                <TouchableOpacity
                  style={[styles.sendBtnWrapper, loading ? {opacity: 0.5} : {}]}
                  onPress={() => {
                    // if (disableSubmitButton) return;
                    if (nextButtonShow) {
                      nextButtonHandler();
                      return;
                    }
                    if (isAiChat && !nextButtonShow) {
                      sendAiMessage();
                    } else {
                      sendMessage();
                    }
                  }}
                  disabled={loading}
                >
                  <Image
                    source={require('../../assets/send-message.svg')}
                    style={{height: 13, width: 13}}
                  />
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>
        </LinearGradient>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  rootContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    top: 0,
    pointerEvents: 'box-none'
  },
  buttonContainer: {
    position: 'absolute',
    bottom: 24,
    right: 20,
    backgroundColor: '#95abbf',
    // backgroundColor: '#067036',
    padding: 2,
    borderRadius: 20,
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    // display: 'none',
  },
  buttonContainerMobile: {
    position: 'absolute',
    height: 40,
    width: 40
  },
  buttonContainerWebLarge: {
    position: 'fixed',
    height: 40,
    width: 40,
    transform: 'translateZ(0)',
    visibility: 'visible'
  },
  buttonContainerWebSmall: {
    position: 'fixed',
    height: 30,
    width: 30,
    transform: 'translateZ(0)',
    visibility: 'visible'
  },
  text: {
    color: 'white',
    fontSize: 28
  },
  borderChatContainer: {
    borderRadius: 10,
    position: 'absolute',
    top: 50,
    right: 0,
    backgroundColor: 'black',
    overflow: 'hidden',
    zIndex: 1001
  },
  chatContainer: {
    backgroundColor: 'black',
    margin: 1,
    borderRadius: 10,
    padding: 10
    // height: 585
  },
  inputGradientBorder: {
    marginTop: 8,
    marginLeft: 6,
    marginRight: 30,
    borderRadius: 10
  },
  inputContainer: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingLeft: 7,
    paddingRight: 18,
    backgroundColor: 'black',
    margin: 1,
    borderRadius: 10
  },
  input: {
    flex: 1,
    color: '#fff',
    paddingRight: 18,
    fontSize: 14,
    overflow: 'hidden'
  },
  sendBtnWrapper: {
    justifyContent: 'center'
  },
  messageWrapper: {
    marginBottom: 10
  },
  userMessageWrapper: {
    alignItems: 'flex-start',
    marginLeft: 6
  },
  companyMessageWrapper: {
    alignItems: 'flex-end',
    marginRight: 6
  },
  messageContainer: {
    borderRadius: 10,
    padding: 10
  },
  companyTextWrapper: {
    marginTop: 10
  },
  messageText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: fontFamily.font400
  },
  nameContainer: {
    position: 'absolute',
    top: -1,
    left: -1,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1
  },
  nameText: {
    color: '#fff',
    fontSize: 10
  },
  scrollViewContainer: {
    flex: 1,
    position: 'relative'
  },
  scrollIndicator: {
    position: 'absolute',
    right: 0,
    width: 8,
    backgroundColor: '#202020',
    borderRadius: 10,
    zIndex: 1000
  },
  chatBtnOptionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  agentChatBtnContainer: {
    width: 100,
    borderRadius: 6,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 20
  },
  agentChatBtn: {
    color: 'white',
    fontSize: 12,
    paddingHorizontal: 5,
    paddingVertical: 3,
    width: 'fit'
  },
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    overflow: 'hidden',
    width: 200,
    height: 18,
    marginBottom: 4
  },
  toggleButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row'
  },
  selectedButton: {
    backgroundColor: '#0586CE'
  },
  unselectedButton: {
    backgroundColor: '#fff'
  },
  toggleText: {
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 5
  },
  selectedText: {
    color: '#fff'
  },
  unselectedText: {
    color: '#0586CE'
  },
  icon: {
    marginRight: 5
  }
});

export default ChatIconSocket;
